import { Button, ModalIsland } from "@saviynt/design-system";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import TicketingSystemModal from "./TicketingSystemModal";
import { useSelection } from "../context/SelectionContext";
import { ACTION_CREATE } from "../../../constants/policies.constants";
import { useAlert } from "../context/AlertContext";
import { useFindingsData } from "../context/FindingsDataContext";
import { useFindingsBulkTicketCreation } from "../../../hooks/usePolicyFindings";
import { FindingsRequestPayload } from "@repo/api/schemas/policiesFindings.schema";
import { parseError } from "../utils";

interface ConfirmationModalProps {
  selectAllItems: boolean;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  payload: FindingsRequestPayload;
  onSuccess: () => void;
}

export default function ConfirmationModal({
  selectAllItems = false,
  isOpen,
  setIsOpen,
  payload,
  onSuccess,
}: ConfirmationModalProps) {
  const intl = useIntl();
  const { selectedRows, clearSelection } = useSelection();
  const { recordsCount } = useFindingsData();

  const [isOnboardTicketingSytem, setIsOnboardTicketingSytem] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasTriggeredAction, setHasTriggeredAction] = useState(false);
  const { showAlert } = useAlert();
  const confirmationMessage = intl.formatMessage(
    {
      defaultMessage:
        "{count} tickets will be created for the selected findings.",
      description: "Confirmation message for ticket creation",
      id: "dPhvOt",
    },
    {
      count: selectAllItems
        ? recordsCount.totalRecordsCount
        : selectedRows.length,
    },
  );

  const violationIds: string[] = [];
  selectedRows.forEach((row: { id: string }) => {
    violationIds.push(row.id);
  });

  const {
    isSuccess: isBulkSuccess,
    isError: isBulkError,
    error: bulkError,
    refetch: refetchBulkCreateTicket,
  } = useFindingsBulkTicketCreation({ violationIds });

  const {
    isSuccess: isSelectAllSuccess,
    isError: isSelectAllError,
    error: selectAllError,
    refetch: refetchSelectAllCreateTicket,
  } = useFindingsBulkTicketCreation({
    findingsFilters: payload,
  });

  useEffect(() => {
    if ((isBulkSuccess || isSelectAllSuccess) && hasTriggeredAction) {
      setIsOpen(false);
      showAlert({ type: ACTION_CREATE });
      setHasTriggeredAction(false);
      onSuccess();
      clearSelection();
    }
  }, [
    clearSelection,
    hasTriggeredAction,
    isBulkSuccess,
    isSelectAllSuccess,
    onSuccess,
    setIsOpen,
    showAlert,
  ]);

  useEffect(() => {
    if ((isBulkError || isSelectAllError) && hasTriggeredAction) {
      const { message } = parseError(bulkError || selectAllError);
      setErrorMessage(message);
      setIsOpen(false);
      setIsOnboardTicketingSytem(true);
      setHasTriggeredAction(false);
    }
  }, [
    bulkError,
    selectAllError,
    hasTriggeredAction,
    isBulkError,
    isSelectAllError,
    setIsOpen,
  ]);

  const handleClick = () => {
    setHasTriggeredAction(true);
    if (selectAllItems) {
      refetchSelectAllCreateTicket();
    } else refetchBulkCreateTicket();
  };

  return (
    <>
      {isOnboardTicketingSytem && (
        <TicketingSystemModal
          subtitle={errorMessage}
          isOpen={isOnboardTicketingSytem}
          setIsOpen={setIsOnboardTicketingSytem}
        />
      )}
      <ModalIsland
        kind="question"
        alertTitle={intl.formatMessage({
          defaultMessage: "Are you sure?",
          description: "Confirmation modal title",
          id: "iChgyz",
        })}
        alertSubtitle={confirmationMessage}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        isOutsideClickEnabled
        primaryButton={
          <Button
            type="button"
            kind="outlined"
            size="medium"
            onClick={handleClick}
          >
            {intl.formatMessage({
              defaultMessage: "Yes, Create Tickets",
              description: "Confirmation modal button text",
              id: "7apOAD",
            })}
          </Button>
        }
        secondaryButton={
          <Button
            type="button"
            kind="ghost"
            size="medium"
            onClick={() => setIsOpen(false)}
          >
            {intl.formatMessage({
              defaultMessage: "Cancel",
              description: "Button to cancel ticket creation confirmation",
              id: "N7630Q",
            })}
          </Button>
        }
      />
    </>
  );
}
